import { NgIf } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import moment from 'moment';
import { Subscription, timer } from 'rxjs';
import { MAINTENANCE_BANNERS, MaintenanceBannerNotice } from './maintenance-banner.config';

const REFRESH_INTERVAL_MS = 60_000;
/** localStorage persists across browser sessions, so a dismissed banner shows again on the next visit. */
export const DISMISSED_KEY_PREFIX = 'miq-maintenance-banner-dismissed:';

function isDismissed(notice: MaintenanceBannerNotice): boolean {
  try {
    return notice.dismissible && localStorage.getItem(DISMISSED_KEY_PREFIX + notice.type) === 'true';
  } catch {
    return false;
  }
}

/** Returns the first notice whose [startsAt, endsAt) range contains `now`. Comparison is on absolute instants. */
export function getActiveMaintenanceBanner(
  now: moment.Moment,
  notices: MaintenanceBannerNotice[] = MAINTENANCE_BANNERS
): MaintenanceBannerNotice | null {
  return (
    notices.find(n => (!n.startsAt || now.isSameOrAfter(moment(n.startsAt))) && now.isBefore(moment(n.endsAt))) ?? null
  );
}

@Component({
  selector: 'miq-maintenance-banner',
  templateUrl: './maintenance-banner.component.html',
  styleUrls: ['./maintenance-banner.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [NgIf],
})
export class MaintenanceBannerComponent implements OnInit, OnDestroy {
  notice: MaintenanceBannerNotice | null = null;
  private subscription: Subscription;

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.refresh();
    this.subscription = timer(REFRESH_INTERVAL_MS, REFRESH_INTERVAL_MS).subscribe(() => {
      this.refresh();
      this.cdr.markForCheck();
    });
  }

  private refresh(): void {
    const active = getActiveMaintenanceBanner(moment());
    this.notice = active && !isDismissed(active) ? active : null;
  }

  dismiss(): void {
    if (!this.notice?.dismissible) {
      return;
    }
    try {
      localStorage.setItem(DISMISSED_KEY_PREFIX + this.notice.type, 'true');
    } catch {
      // storage unavailable: hide for this page view only
    }
    this.notice = null;
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }
}
